import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarBlank, 
  Clock, 
  User, 
  EnvelopeSimple, 
  Phone,
  Buildings,
  Check,
  ArrowLeft,
  Hash,
  CurrencyCircleDollar,
  ChatCircleText
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LegalConsent from '../components/LegalConsent';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const consultationTypes = [
  {
    id: 'session-30',
    title: 'جلسة قصيرة',
    titleEn: '30 minutes',
    duration: '٣٠ دقيقة',
    price: 199,
    description: 'استشارة سريعة موجّهة لسؤال محدد أو فكرة أولية',
  },
  {
    id: 'session-60',
    title: 'جلسة متوسطة',
    titleEn: '60 minutes',
    duration: '٦٠ دقيقة',
    price: 390,
    description: 'مناقشة موسّعة لتحدي قائم أو خطة عمل بسيطة',
  },
  {
    id: 'session-120',
    title: 'جلسة متعمّقة',
    titleEn: '120 minutes',
    duration: '١٢٠ دقيقة',
    price: 690,
    description: 'جلسة معمّقة لقراءة وضع المنشأة وبناء توصيات أولية',
  },
];

const timeSlots = [
  '09:00', '10:00', '11:00', '14:00', '15:00', '16:00'
];

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setConsentError('يجب الموافقة على سياسة الخصوصية والشروط والأحكام قبل الحجز');
      toast.error('يرجى الموافقة على السياسات قبل المتابعة');
      return;
    }
    if (!selectedType || !selectedDate || !selectedTime) {
      toast.error('يرجى إكمال جميع خطوات الحجز');
      return;
    }
    setIsSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/consultation-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          organization: formData.organization,
          message: formData.message,
          consultation_type: selectedType.id,
          preferred_date: selectedDate,
          preferred_time: selectedTime,
          consent_accepted: true,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'تعذّر إرسال الطلب');
      }
      const data = await res.json();
      setBookingResult(data);
      setIsBooked(true);
      toast.success('تم حجز الاستشارة بنجاح!');
    } catch (err) {
      toast.error(err.message || 'حدث خطأ أثناء إرسال الطلب');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToStep2 = selectedType !== null;
  const canProceedToStep3 = selectedDate && selectedTime;

  if (isBooked) {
    const shortRef = (bookingResult?.id || '').slice(0, 8).toUpperCase();
    const label = bookingResult?.consultation_label_ar || selectedType?.title || '';
    const price = bookingResult?.price_sar ?? selectedType?.price ?? 0;
    return (
      <main data-testid="booking-success" className="pt-20 min-h-screen bg-[#FAFAF7]">
        <section className="py-16 sm:py-24">
          <div className="max-w-xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl border border-[#D5D1C8] p-8 sm:p-12 text-center shadow-sm"
            >
              <div className="w-20 h-20 rounded-full bg-[#4A5D23] flex items-center justify-center mx-auto mb-6">
                <Check size={40} className="text-[#FAFAF7]" weight="bold" />
              </div>
              <h2 className="text-3xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-3">
                تم استلام طلب الحجز
              </h2>
              <p className="text-[#7A8A62] mb-6 leading-8">
                سيتواصل معك فريقنا خلال يوم عمل لتأكيد الموعد وإرسال رابط الجلسة إلى بريدك الإلكتروني.
              </p>

              {shortRef && (
                <div
                  data-testid="booking-reference"
                  className="bg-[#F3EFE9] rounded-xl px-5 py-4 mb-6 inline-flex items-center gap-3"
                >
                  <Hash size={18} className="text-[#4A5D23]" />
                  <span className="text-xs text-[#7A8A62]">رقم الطلب</span>
                  <span className="font-mono text-lg font-bold text-[#1A210D]" dir="ltr">
                    {shortRef}
                  </span>
                </div>
              )}

              <div className="bg-[#F3EFE9] rounded-2xl p-6 text-right mb-6 space-y-4">
                {label && (
                  <div className="flex items-center gap-3">
                    <ChatCircleText size={20} className="text-[#4A5D23] shrink-0" />
                    <div className="flex flex-col">
                      <span className="text-xs text-[#7A8A62]">نوع الجلسة</span>
                      <span className="text-[#1A210D] font-medium">{label}</span>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <CalendarBlank size={20} className="text-[#4A5D23] shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-xs text-[#7A8A62]">التاريخ المقترح</span>
                    <span className="text-[#1A210D] font-medium" dir="ltr">{selectedDate}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-[#4A5D23] shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-xs text-[#7A8A62]">الوقت المقترح</span>
                    <span className="text-[#1A210D] font-medium" dir="ltr">{selectedTime}</span>
                  </div>
                </div>
                {price > 0 && (
                  <div className="flex items-center gap-3 pt-3 border-t border-[#D5D1C8]">
                    <CurrencyCircleDollar size={20} className="text-[#4A5D23] shrink-0" />
                    <div className="flex flex-col">
                      <span className="text-xs text-[#7A8A62]">قيمة الجلسة</span>
                      <span className="text-[#1A210D] font-medium">
                        {price.toLocaleString()} ر.س
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-[#7A8A62] mb-8 leading-6">
                احتفظ برقم الطلب للرجوع إليه عند التواصل معنا. تُحتسب قيمة الجلسة ضمن العقد النهائي عند التعاقد على الخدمة.
              </p>

              <Button
                data-testid="booking-return-home"
                onClick={() => window.location.href = '/'}
                className="bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl px-8 py-3"
              >
                العودة للرئيسية
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="booking-page" className="pt-20 min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <section className="py-12 bg-[#F3EFE9]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 bg-[#FAFAF7] text-[#3C4A20] text-sm font-medium rounded-full border border-[#D5D1C8] mb-6">
            الجلسات الاستشارية
          </span>
          <h1 className="text-4xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-4">
            احجزي جلستك الاستشارية
          </h1>
          <p className="text-base sm:text-lg text-[#7A8A62] max-w-2xl mx-auto leading-8">
            الجلسات مخصصة للجهات والمنشآت والمهتمين بتطوير خدمات الطفولة المبكرة.
          </p>
          <p className="text-sm text-[#4A5D23] mt-4 bg-white/60 inline-block px-4 py-2 rounded-full">
            تُحتسب قيمة الجلسة ضمن العقد النهائي عند التعاقد على الخدمة.
          </p>
        </div>
      </section>

      {/* Progress */}
      <section className="py-6 border-b border-[#D5D1C8]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: 'نوع الاستشارة' },
              { num: 2, label: 'الموعد' },
              { num: 3, label: 'البيانات' },
            ].map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s.num 
                    ? 'bg-[#2C3814] text-[#FAFAF7]' 
                    : 'bg-[#D5D1C8] text-[#7A8A62]'
                }`}>
                  {step > s.num ? <Check size={16} /> : s.num}
                </div>
                <span className={`mr-2 text-sm hidden sm:inline ${step >= s.num ? 'text-[#2C3814]' : 'text-[#7A8A62]'}`}>
                  {s.label}
                </span>
                {index < 2 && (
                  <div className={`w-8 sm:w-16 h-0.5 mx-2 ${step > s.num ? 'bg-[#2C3814]' : 'bg-[#D5D1C8]'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Step 1: Consultation Type */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-6 text-center">
                اختر نوع الاستشارة
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {consultationTypes.map((type) => (
                  <button
                    key={type.id}
                    data-testid={`consultation-type-${type.id}`}
                    onClick={() => setSelectedType(type)}
                    className={`p-6 rounded-2xl border-2 text-right transition-all ${
                      selectedType?.id === type.id
                        ? 'border-[#2C3814] bg-[#2C3814]/5'
                        : 'border-[#D5D1C8] bg-[#FAFAF7] hover:border-[#7A8A62]'
                    }`}
                  >
                    <h3 className="font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-1">
                      {type.title}
                    </h3>
                    <span className="text-xs text-[#7A8A62] block mb-3">{type.titleEn}</span>
                    <div className="flex items-center gap-2 text-sm text-[#4A5D23] mb-2">
                      <Clock size={16} />
                      {type.duration}
                    </div>
                    <p className="text-sm text-[#3C4A20] mb-3">{type.description}</p>
                    <div className="pt-3 border-t border-[#D5D1C8]">
                      <span className="text-2xl font-bold text-[#2C3814]">{type.price.toLocaleString()}</span>
                      <span className="text-sm text-[#7A8A62] mr-1">ريال</span>
                    </div>
                    {selectedType?.id === type.id && (
                      <div className="mt-4 flex justify-end">
                        <Check size={20} className="text-[#2C3814]" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="text-center">
                <Button
                  data-testid="proceed-to-date"
                  onClick={() => setStep(2)}
                  disabled={!canProceedToStep2}
                  className="bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl px-8 py-4 disabled:opacity-50"
                >
                  التالي
                  <ArrowLeft size={20} className="mr-2 rtl-flip" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-6 text-center">
                اختر الموعد المناسب
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Date */}
                <div>
                  <Label className="mb-3 block">التاريخ</Label>
                  <Input
                    type="date"
                    data-testid="booking-date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="mb-3 block">الوقت</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        data-testid={`time-slot-${time}`}
                        onClick={() => setSelectedTime(time)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          selectedTime === time
                            ? 'bg-[#2C3814] text-[#FAFAF7]'
                            : 'bg-[#F3EFE9] text-[#3C4A20] hover:bg-[#E8E4DC]'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="border-[#D5D1C8] text-[#2C3814] rounded-xl px-6 py-3"
                >
                  رجوع
                </Button>
                <Button
                  data-testid="proceed-to-form"
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                  className="bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl px-8 py-3 disabled:opacity-50"
                >
                  التالي
                  <ArrowLeft size={20} className="mr-2 rtl-flip" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Form */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-bold text-[#1A210D] font-['IBM_Plex_Sans_Arabic'] mb-6 text-center">
                بيانات الحجز
              </h2>

              {/* Summary */}
              <div className="bg-[#F3EFE9] rounded-2xl p-6 mb-8">
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <CalendarBlank size={20} className="text-[#4A5D23]" />
                    <span className="text-[#1A210D]">{selectedDate}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={20} className="text-[#4A5D23]" />
                    <span className="text-[#1A210D]">{selectedTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#7A8A62]">{selectedType?.title}</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-testid="booking-form">
                <div className="bg-[#FAFAF7] rounded-3xl border border-[#D5D1C8] p-8">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User size={16} className="text-[#7A8A62]" />
                        الاسم الكامل *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="booking-name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <EnvelopeSimple size={16} className="text-[#7A8A62]" />
                        البريد الإلكتروني *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="booking-email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone size={16} className="text-[#7A8A62]" />
                        رقم الهاتف *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="booking-phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization" className="flex items-center gap-2">
                        <Buildings size={16} className="text-[#7A8A62]" />
                        اسم الجهة
                      </Label>
                      <Input
                        id="organization"
                        name="organization"
                        data-testid="booking-organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        className="rounded-xl h-12 bg-[#F3EFE9] border-[#D5D1C8]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 mb-8">
                    <Label htmlFor="message">رسالة أو ملاحظات</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="booking-message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="rounded-xl bg-[#F3EFE9] border-[#D5D1C8] resize-none"
                      placeholder="أخبرنا عن احتياجاتك أو أسئلتك..."
                    />
                  </div>

                  <div className="flex gap-4">
                  <LegalConsent
                    checked={consent}
                    onChange={(v) => { setConsent(v); if (v) setConsentError(''); }}
                    error={consentError}
                    testId="booking-legal-consent"
                  />
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-[#D5D1C8] text-[#2C3814] rounded-xl py-4"
                    >
                      رجوع
                    </Button>
                    <Button
                      type="submit"
                      data-testid="submit-booking"
                      disabled={isSubmitting}
                      className="flex-1 bg-[#2C3814] hover:bg-[#3C4A20] text-[#FAFAF7] rounded-xl py-4"
                    >
                      {isSubmitting ? 'جاري الحجز...' : 'تأكيد الحجز'}
                    </Button>
                  </div>

                  <ul className="mt-8 space-y-2.5 text-sm text-[#3C4A20]">
                    <li className="flex items-center gap-2">
                      <Check size={16} className="text-[#4A5D23]" />
                      سيتم إرسال رابط الجلسة إلى بريدك الإلكتروني
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={16} className="text-[#4A5D23]" />
                      يمكن إعادة جدولة الموعد قبل ٢٤ ساعة
                    </li>
                    <li className="flex items-center gap-2">
                      <Check size={16} className="text-[#4A5D23]" />
                      جميع معلوماتك سرية ومحفوظة
                    </li>
                  </ul>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

export default BookingPage;
